import fetcher from '@/core/lib/fetcher'
import { APIResponse } from '@/core/types/utils'
import { queryOptions } from '@tanstack/react-query'
import type { ConnectionPayload } from './buildConnectionPayload'
import type { Connection } from './connectivity.types'

export const getConnections = (siteId: string, token: string) =>
  queryOptions({
    queryKey: ['connections', siteId, token],
    queryFn: () =>
      fetcher
        .auth(`Bearer ${token}`)
        .url(
          import.meta.env.VITE_API_BOOKING +
            `property/${siteId}/connections`
        )
        .get()
        .json<APIResponse<Connection[]>>(),
  })

// Inventory only uses the connections to lock manual edits on PMS-connected
// properties, so a failing endpoint (integration-service down) must not take
// the calendar down with it. The failure is reported as `unavailable` and
// treated as "no connection" (editable) with a visible warning: product call,
// editing stays possible and the warning tells the user the lock is unknown.
// Separate key suffix so the Advanced tab keeps the strict query.
export const getInventoryConnections = (siteId: string, token: string) =>
  queryOptions({
    queryKey: ['connections', siteId, token, 'inventory'],
    queryFn: async (): Promise<{
      connections: Connection[]
      unavailable: boolean
    }> => {
      try {
        const res = await fetcher
          .auth(`Bearer ${token}`)
          .url(
            import.meta.env.VITE_API_BOOKING +
              `property/${siteId}/connections`
          )
          .get()
          .json<APIResponse<Connection[]>>()
        return { connections: res.data ?? [], unavailable: false }
      } catch {
        return { connections: [], unavailable: true }
      }
    },
  })

export const addConnection = (
  siteId: string,
  token: string,
  // Built by buildConnectionPayload, never the raw form values: the backend
  // rejects unknown keys and empty Mews tokens.
  connectionData: ConnectionPayload
) =>
  fetcher
    .auth(`Bearer ${token}`)
    .url(
      import.meta.env.VITE_API_BOOKING + `property/${siteId}/connections`
    )
    .post(connectionData)
    .json<APIResponse<Connection>>()

export const disconnectConnection = (
  siteId: string,
  id: string,
  token: string
) =>
  fetcher
    .auth(`Bearer ${token}`)
    .url(
      import.meta.env.VITE_API_BOOKING +
        `property/${siteId}/connections/${id}`
    )
    .delete()
    .json<APIResponse<string>>()

export const updatePricing = (siteId: string, token: string) =>
  fetcher
    .auth(`Bearer ${token}`)
    .url(
      import.meta.env.VITE_API_BOOKING +
        `property/${siteId}/integration/refresh-prices?type=Room`
    )
    .get()
    .json<APIResponse<string>>()
